import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import ExperienceButton from './ExperienceButton'
import { ExperienceSelect } from '../form'
import {
  buildCoachSchedulingFlagsMapFromOptions,
  getUniqueCoachSchedulingIssues,
} from '../scheduling/schedulingPreconditions'
import {
  getGuideId,
  getParticipantEmail,
  getParticipantGuides,
  getParticipantId,
  getParticipantName,
} from './utils/guidePairingUtils'

export const PARTICIPANT_TAB = 'participant'
export const GUIDE_TAB = 'guide'

const newGuideRow = () => ({ id: Date.now(), guideId: '', clientIds: [] })

export const getInitialPairingState = (participants = []) => ({
  tab: PARTICIPANT_TAB,
  participantAssignments: participants.reduce((acc, p) => {
    acc[getParticipantId(p)] = ''
    return acc
  }, {}),
  guideRows: [newGuideRow()],
})

export const collectPairs = (state) => {
  if (state.tab === PARTICIPANT_TAB) {
    return Object.entries(state.participantAssignments)
      .filter(([, guideId]) => guideId)
      .map(([clientId, coachId]) => ({ clientId, coachId }))
  }
  const pairs = []
  state.guideRows.forEach((row) => {
    if (!row.guideId || row.clientIds.length === 0) return
    row.clientIds.forEach((clientId) => {
      pairs.push({ clientId, coachId: row.guideId })
    })
  })
  return pairs
}

const GuideSelect = ({ value, options, disabled, placeholder, onChange }) => (
  <ExperienceSelect
    className='experience-gp-guide-select'
    value={value}
    options={options}
    disabled={disabled}
    placeholder={placeholder}
    isClearable
    onChange={(e) => onChange(e.target.value)}
  />
)

const ParticipantMultiSelect = ({
  options,
  selectedIds,
  disabled,
  onChange,
  nameById,
}) => {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)
  console.log('selectedIds', selectedIds)
  useEffect(() => {
    if (!open) return undefined
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  const toggle = (id) => {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((x) => x !== id)
        : [...selectedIds, id],
    )
  }

  return (
    <div ref={wrapRef} className={classNames('experience-gp-multi', { open })}>
      <button
        type='button'
        className='experience-f-control experience-gp-multi-toggle'
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}>
        {selectedIds.length
          ? `${selectedIds.length} selected`
          : 'Select participants'}
      </button>
      {open ? (
        <div className='experience-gp-multi-menu'>
          {options.length ? (
            options.map((p) => {
              const id = getParticipantId(p)
              return (
                <label key={id}>
                  <input
                    type='checkbox'
                    className='experience-gp-check'
                    checked={selectedIds.includes(id)}
                    onChange={() => toggle(id)}
                  />
                  {getParticipantName(p)}
                </label>
              )
            })
          ) : (
            <div className='experience-gp-empty-opt'>
              No participant available right now
            </div>
          )}
        </div>
      ) : null}
      {selectedIds.length ? (
        <div className='experience-gp-chips'>
          {selectedIds.map((id) => (
            <span className='experience-gp-chip' key={id}>
              {nameById[id] || id}
              <button
                type='button'
                aria-label='Remove'
                disabled={disabled}
                onClick={() => toggle(id)}>
                ×
              </button>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  )
}

const QuestGuidePairingModal = ({
  show,
  participants,
  guides,
  isLoadingGuides,
  pairingState,
  setPairingState,
  onCancel,
  onNext,
}) => {
  const [schedulingIssues, setSchedulingIssues] = useState([])

  useEffect(() => {
    if (show) setSchedulingIssues([])
  }, [show])

  useEffect(() => {
    if (!show) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [show, onCancel])

  if (!show) return null

  const continueToSOW = (issues) => {
    onNext(collectPairs(pairingState), {
      coachSchedulingFlagsByCoachId:
        buildCoachSchedulingFlagsMapFromOptions(guides),
      forceManualScheduling: issues.length > 0,
    })
  }

  const handleNext = () => {
    const assignments = collectPairs(pairingState).map(
      ({ clientId, coachId }) => ({ userId: clientId, coachId }),
    )
    const users = participants.map((p) => ({
      _id: getParticipantId(p),
      fullName: getParticipantName(p),
    }))
    const issues = getUniqueCoachSchedulingIssues(assignments, guides, users)
    if (issues.length > 0) {
      setSchedulingIssues(issues)
      return
    }
    continueToSOW(issues)
  }

  if (schedulingIssues.length > 0) {
    return (
      <div
        className='experience-gp-modal-backdrop'
        role='dialog'
        aria-modal='true'
        aria-labelledby='gpSchedulingTitle'>
        <div className='experience-gp-pair-card experience-gp-scheduling-card'>
          <div className='experience-gp-pair-head'>
            <h2 id='gpSchedulingTitle'>Platform scheduling unavailable</h2>
            <button
              type='button'
              className='experience-gp-x'
              aria-label='Close'
              onClick={onCancel}>
              ×
            </button>
          </div>
          <p className='experience-f-hint experience-gp-lead'>
            The following guide
            {schedulingIssues.length > 1 ? 's are' : ' is'} not set up for
            platform scheduling. SOWs will be created with manual scheduling
            instead.
          </p>
          <ul className='experience-gp-issues'>
            {schedulingIssues.map((issue) => (
              <li key={issue.coachId}>
                <b>{issue.coachName}</b>
                <span>{issue.message}</span>
                {issue.clients?.length ? (
                  <small>Paired with {issue.clients.join(', ')}</small>
                ) : null}
              </li>
            ))}
          </ul>
          <p className='experience-f-hint'>Do you want to continue?</p>
          <div className='experience-gp-modal-actions'>
            <ExperienceButton
              variant='outline'
              onClick={() => setSchedulingIssues([])}>
              Back
            </ExperienceButton>
            <ExperienceButton
              variant='primary'
              onClick={() => continueToSOW(schedulingIssues)}>
              Next
            </ExperienceButton>
          </div>
        </div>
      </div>
    )
  }

  const { tab, participantAssignments, guideRows } = pairingState
  const guidePlaceholder = isLoadingGuides
    ? 'Fetching Guides...'
    : 'Select Guide'
  const nameById = participants.reduce((acc, p) => {
    acc[getParticipantId(p)] = getParticipantName(p)
    return acc
  }, {})

  // Guides already paired with a participant are listed first.
  const guidesForParticipant = (p) => {
    const assignedIds = getParticipantGuides(p).map(getGuideId)
    if (assignedIds.length === 0) return guides
    const assigned = assignedIds
      .map((id) => guides.find((g) => g.value === id))
      .filter(Boolean)
    return [
      ...assigned,
      ...guides.filter((g) => !assignedIds.includes(g.value)),
    ]
  }

  const setParticipantGuide = (participantId, guideId) => {
    setPairingState((prev) => ({
      ...prev,
      participantAssignments: {
        ...prev.participantAssignments,
        [participantId]: guideId,
      },
    }))
  }

  const updateGuideRow = (index, patch) => {
    setPairingState((prev) => {
      const rows = prev.guideRows.map((row, i) =>
        i === index ? { ...row, ...patch } : row,
      )
      const last = rows[rows.length - 1]
      if (last.guideId && last.clientIds.length > 0) {
        rows.push(newGuideRow())
      }
      return { ...prev, guideRows: rows }
    })
  }

  const usedGuideIds = (exceptIndex) =>
    guideRows
      .filter((row, i) => i !== exceptIndex && row.guideId)
      .map((row) => row.guideId)

  const usedClientIds = (exceptIndex) =>
    guideRows
      .filter((_, i) => i !== exceptIndex)
      .reduce((acc, row) => acc.concat(row.clientIds), [])

  const hasSelection = collectPairs(pairingState).length > 0

  return (
    <div
      className='experience-gp-modal-backdrop'
      role='dialog'
      aria-modal='true'
      aria-labelledby='gpPairTitle'>
      <div className='experience-gp-pair-card'>
        <div className='experience-gp-pair-head'>
          <h2 id='gpPairTitle'>Select Guide Pairing</h2>
          <button
            type='button'
            className='experience-gp-x'
            aria-label='Close'
            onClick={onCancel}>
            ×
          </button>
        </div>
        <p className='experience-f-hint experience-gp-lead'>
          Choose a wisdom guide for each selected participant, or work
          guide-wise. Nothing is saved until the SOW is assigned.
        </p>
        <div className='experience-mini-tabs'>
          <button
            type='button'
            className={tab === PARTICIPANT_TAB ? 'sel' : undefined}
            onClick={() =>
              setPairingState((prev) => ({ ...prev, tab: PARTICIPANT_TAB }))
            }>
            Participant wise
          </button>
          <button
            type='button'
            className={tab === GUIDE_TAB ? 'sel' : undefined}
            onClick={() =>
              setPairingState((prev) => ({ ...prev, tab: GUIDE_TAB }))
            }>
            Coach wise
          </button>
        </div>

        <div className='experience-gp-pair-body'>
          {tab === PARTICIPANT_TAB ? (
            <table className='experience-tbl experience-gp-modal-tbl'>
              <thead>
                <tr>
                  <th>Participant</th>
                  <th>Wisdom Guide</th>
                </tr>
              </thead>
              <tbody>
                {participants.map((p) => {
                  const id = getParticipantId(p)
                  const email = getParticipantEmail(p)
                  return (
                    <tr key={id}>
                      <td className='experience-who'>
                        <b>{getParticipantName(p)}</b>
                        {email ? <span>{email}</span> : null}
                      </td>
                      <td>
                        <GuideSelect
                          value={participantAssignments[id] || ''}
                          options={guidesForParticipant(p)}
                          disabled={isLoadingGuides}
                          placeholder={guidePlaceholder}
                          onChange={(guideId) =>
                            setParticipantGuide(id, guideId)
                          }
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          ) : (
            <table className='experience-tbl experience-gp-modal-tbl'>
              <thead>
                <tr>
                  <th>Wisdom Guide</th>
                  <th>Participants</th>
                </tr>
              </thead>
              <tbody>
                {guideRows.map((row, index) => {
                  const takenGuides = usedGuideIds(index)
                  const takenClients = usedClientIds(index)
                  return (
                    <tr key={row.id}>
                      <td>
                        <GuideSelect
                          value={row.guideId}
                          options={guides.filter(
                            (g) => !takenGuides.includes(g.value),
                          )}
                          disabled={isLoadingGuides}
                          placeholder={guidePlaceholder}
                          onChange={(guideId) =>
                            updateGuideRow(index, { guideId })
                          }
                        />
                      </td>
                      <td>
                        <ParticipantMultiSelect
                          options={participants.filter(
                            (p) => !takenClients.includes(getParticipantId(p)),
                          )}
                          selectedIds={row.clientIds}
                          disabled={isLoadingGuides}
                          nameById={nameById}
                          onChange={(clientIds) =>
                            updateGuideRow(index, { clientIds })
                          }
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className='experience-gp-modal-actions'>
          <ExperienceButton variant='outline' onClick={onCancel}>
            Cancel
          </ExperienceButton>
          <ExperienceButton
            variant='primary'
            disabled={isLoadingGuides || !hasSelection}
            onClick={handleNext}>
            Next
          </ExperienceButton>
        </div>
      </div>
    </div>
  )
}

QuestGuidePairingModal.propTypes = {
  show: PropTypes.bool,
  participants: PropTypes.arrayOf(PropTypes.object),
  guides: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
      fullName: PropTypes.string,
      isScheduleEnabled: PropTypes.bool,
      hasVideoCallLink: PropTypes.bool,
    }),
  ),
  isLoadingGuides: PropTypes.bool,
  pairingState: PropTypes.shape({
    tab: PropTypes.string,
    participantAssignments: PropTypes.object,
    guideRows: PropTypes.array,
  }).isRequired,
  setPairingState: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
}

QuestGuidePairingModal.defaultProps = {
  show: false,
  participants: [],
  guides: [],
  isLoadingGuides: false,
}

export default QuestGuidePairingModal
